// lib/footfall/openTrafficData.ts
import type { AcquiredTrafficSource, TrafficDataPack, TrafficObservation } from '#types/types';
import { POPULAR_DAYS } from '#lib/footfall/popularTimes';

export const IDFM_DOCUMENTATION = 'https://eu.ftp.opendatasoft.com/stif/Validations/Documentation/Donnees_de_validation.pdf';
export const IDFM_DAY_TYPES: Record<string, string> = {
  JOHV: 'Jour ouvré hors vacances scolaires', JOVS: 'Jour ouvré en vacances scolaires',
  SAHV: 'Samedi hors vacances scolaires', SAVS: 'Samedi en vacances scolaires',
  DIJFP: 'Dimanche, jours fériés et ponts',
};
const object = (v: unknown): Record<string, unknown> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw Error('Expected an object');
  return v as Record<string, unknown>;
};
const text = (v: unknown): string => { if (typeof v !== 'string' || !v.trim()) throw Error('Missing text field'); return v; };
const number = (v: unknown): number => { if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) throw Error('Invalid non-negative number'); return v; };
const integer = (v: unknown): number => { const n = number(v); if (!Number.isSafeInteger(n)) throw Error('Invalid integer count'); return n; };
const date = (v: unknown): string => {
  const s = text(v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || !Number.isFinite(Date.parse(s)) || new Date(s).toISOString().slice(0, 10) !== s) throw Error('Invalid measurement date');
  return s;
};
const key = (...parts: unknown[]) => JSON.stringify(parts);
const exact = (v: unknown) => v === null ? { value: null, bounds: null, rawValue: null } : { value: integer(v), bounds: { lower: integer(v), upper: integer(v) }, rawValue: integer(v) };

/** Same natural key and same content is idempotent; conflicting source observations fail the dataset. */
export function uniqueTrafficObservations(rows: TrafficObservation[]): TrafficObservation[] {
  const unique = new Map<string, TrafficObservation>();
  for (const row of rows) {
    const prior = unique.get(row.id);
    if (prior && JSON.stringify(prior) !== JSON.stringify(row)) throw Error(`Conflicting observation: ${row.id}`);
    unique.set(row.id, row);
  }
  return [...unique.values()].sort((a, b) => a.id.localeCompare(b.id));
}

export function sncfAnnualObservations(source: AcquiredTrafficSource, uic: string): TrafficObservation[] {
  if (source.records.length !== 1) throw Error('SNCF exact station must return one record');
  const row = object(source.records[0]);
  if (row.code_uic_complet !== uic) throw Error('Wrong SNCF station identity');
  const output: TrafficObservation[] = [];
  // Explicit traveller-series fields only: totals including non-travellers are a separate population.
  for (let year = 2015; year <= 2030; year++) {
    const field = year === 2017 ? 'totalvoyageurs2017' : `total_voyageurs_${year}`;
    if (!(field in row)) continue;
    output.push({ id: key(source.receipt.id, uic, year, 'travellers'), sourceId: source.receipt.id,
      subject: { kind: 'station', id: `uic:${uic}`, name: text(row.nom_gare) },
      metric: 'station_passenger_movements', unit: 'passengers', basis: 'provider_estimate', ...exact(row[field]),
      period: { from: `${year}-01-01`, to: `${year}-12-31`, timezone: 'Europe/Paris' }, dimensions: { population: 'travellers' } });
  }
  if (!output.length) throw Error('SNCF traveller series is absent');
  return output;
}

function idfmStation(row: Record<string, unknown>, id: number) {
  if (row.id_zdc !== id) throw Error('Wrong IDFM station identity');
  return { operator: integer(row.code_stif_trns), network: text(row.code_stif_res), stop: text(row.code_stif_arret) };
}
function idfmRelease(metadata: unknown): string {
  const title = text(object(object(object(metadata).metas).default).title);
  const match = title.match(/([1-4])(?:er|ème|eme|e)?\s+trimestre\s+(20\d{2})/i);
  if (!match) throw Error('IDFM release period is absent from official title');
  return `${match[2]}-Q${match[1]}`;
}

export function idfmDailyObservations(source: AcquiredTrafficSource, stationId: number): TrafficObservation[] {
  const release = idfmRelease(source.metadata);
  const output = source.records.map(value => {
    const row = object(value), dimensions = { ...idfmStation(row, stationId), ticketCategory: text(row.categorie_titre) };
    if (/^(total|ensemble|all)$/i.test(dimensions.ticketCategory)) throw Error('Overlapping total category cannot be summed');
    const operatingDate = date(row.jour), month = Number(operatingDate.slice(5, 7));
    if (`${operatingDate.slice(0, 4)}-Q${Math.ceil(month / 3)}` !== release) throw Error('Daily observation disagrees with source release');
    const count = exact(row.nb_vald);
    // Source sentinel: low counts are published as 5. Never invent an exact count or midpoint.
    const observed = count.value === 5 ? { value: null, bounds: { lower: 0, upper: 5 }, rawValue: 5 } : count;
    return { id: key(source.receipt.id, stationId, dimensions.operator, dimensions.network, dimensions.stop, operatingDate, dimensions.ticketCategory), sourceId: source.receipt.id,
      subject: { kind: 'station' as const, id: `idfm-zdc:${stationId}`, name: text(row.libelle_arret) }, metric: 'station_validation_entries' as const,
      unit: 'validations' as const, basis: 'recorded_events' as const, ...observed,
      period: { from: operatingDate, to: operatingDate, timezone: 'Europe/Paris' as const, operatingDayStartsAt: 4 as const }, dimensions };
  });
  return uniqueTrafficObservations(output);
}

export function idfmHourlyObservations(source: AcquiredTrafficSource, stationId: number): TrafficObservation[] {
  idfmRelease(source.metadata);
  return uniqueTrafficObservations(source.records.map(value => {
    const row = object(value), identity = idfmStation(row, stationId), dayType = text(row.cat_jour), timeBand = text(row.trnc_horr_60);
    if (!Object.hasOwn(IDFM_DAY_TYPES, dayType)) throw Error('Unknown IDFM day type');
    const match = timeBand.match(/^(\d{1,2})H-(\d{1,2})H$/);
    if (!match || Number(match[1]) > 23 || Number(match[2]) !== (Number(match[1]) + 1) % 24) throw Error('Invalid hourly time band');
    const percent = number(row.pourcentage_validations);
    if (percent > 100) throw Error('Invalid percentage');
    const dimensions = { ...identity, dayType, dayTypeMeaning: IDFM_DAY_TYPES[dayType]!, timeBand, hour: Number(match[1]), denominator: 'Station daily validations for this source day type' };
    return { id: key(source.receipt.id, stationId, identity.operator, identity.network, identity.stop, dayType, timeBand), sourceId: source.receipt.id,
      subject: { kind: 'station' as const, id: `idfm-zdc:${stationId}`, name: text(row.libelle_arret) },
      metric: 'station_validation_share' as const, unit: 'percent' as const, basis: 'relative_index' as const,
      value: percent, bounds: null, rawValue: percent, period: null, dimensions };
  }));
}

export function hourlyTrafficCoverage(observations: TrafficObservation[]): TrafficDataPack['hourlyCoverage'] {
  const groups = new Map<string, TrafficObservation[]>();
  for (const row of observations.filter(o => o.metric === 'station_validation_share')) {
    const id = key(row.subject.id, row.dimensions.operator, row.dimensions.network, row.dimensions.stop, row.dimensions.dayType);
    groups.set(id, [...(groups.get(id) ?? []), row]);
  }
  return [...groups.values()].map(rows => {
    const hours = rows.map(r => Number(r.dimensions.hour)).sort((a, b) => a - b);
    if (new Set(hours).size !== hours.length) throw Error('Duplicate hourly bin');
    const sumPercent = rows.reduce((sum, r) => sum + (r.value ?? 0), 0);
    if (Math.abs(sumPercent - 100) > 0.050001) throw Error('Hourly profile denominator is incomplete or invalid');
    return { subjectId: rows[0]!.subject.id, dayType: String(rows[0]!.dimensions.dayType), hours,
      missingHours: Array.from({ length: 24 }, (_, h) => h).filter(h => !hours.includes(h)), sumPercent, measurementPeriod: null };
  });
}

export function dailyTrafficTotals(observations: TrafficObservation[]): TrafficDataPack['dailyValidationTotals'] {
  const groups = new Map<string, TrafficObservation[]>();
  for (const row of uniqueTrafficObservations(observations).filter(o => o.metric === 'station_validation_entries')) {
    const id = key(row.subject.id, row.period!.from);
    groups.set(id, [...(groups.get(id) ?? []), row]);
  }
  return [...groups.values()].map(rows => {
    const known = rows.every(r => r.bounds !== null);
    return { operatingDate: rows[0]!.period!.from, subjectId: rows[0]!.subject.id,
      value: rows.every(r => r.value !== null) ? rows.reduce((sum, r) => sum + r.value!, 0) : null,
      bounds: known ? { lower: rows.reduce((sum, r) => sum + r.bounds!.lower, 0), upper: rows.reduce((sum, r) => sum + r.bounds!.upper, 0) } : null,
      rows: rows.length, derivedFrom: rows.map(r => r.id) };
  }).sort((a, b) => a.operatingDate.localeCompare(b.operatingDate));
}

export function buildTrafficDataPack(input: unknown, inputSha256: string, generatedAt: string, sources: AcquiredTrafficSource[], station: { uic: string; idfm: number }): TrafficDataPack {
  const snapshot = object(input), audits = snapshot.rows;
  if (!Array.isArray(audits)) throw Error('Missing scoped audit snapshot');
  const propertyId = text(snapshot.propertyId), organisationId = text(snapshot.organisationId);
  const audit = (procedure: string) => {
    const matches = audits.map(object).filter(a => a.procedureName === procedure);
    if (matches.length !== 1 || matches[0]!.entityId !== propertyId || matches[0]!.organisationId !== organisationId || matches[0]!.entityType !== 'Property') throw Error('Invalid scoped input audit');
    return matches[0]!;
  };
  const baseAudit = audit('FATER_FOOTFALL_BASE_V1'), activityAudit = audit('FATER_FOOTFALL_BUSYNESS_V1');
  const census = object(baseAudit.suggestedFix).shops, profiles = object(activityAudit.suggestedFix).shops;
  if (!Array.isArray(census) || !Array.isArray(profiles)) throw Error('Missing census or activity profiles');
  const places = census.map(object), profileMap = new Map<string, Record<string, unknown>>();
  const ids = places.map(p => text(p.placeId));
  if (new Set(ids).size !== ids.length) throw Error('Duplicate census identity');
  for (const p of places) {
    text(p.name);
    if (p.lat !== null && p.lat !== undefined && (typeof p.lat !== 'number' || !Number.isFinite(p.lat) || Math.abs(p.lat) > 90)) throw Error('Invalid census latitude');
    if (p.lng !== null && p.lng !== undefined && (typeof p.lng !== 'number' || !Number.isFinite(p.lng) || Math.abs(p.lng) > 180)) throw Error('Invalid census longitude');
  }
  const activity: TrafficObservation[] = [];
  for (const item of profiles) {
    const profile = object(item), placeId = text(profile.placeId);
    if (!ids.includes(placeId) || profileMap.has(placeId)) throw Error('Unmatched or duplicate activity identity');
    profileMap.set(placeId, profile);
    for (const [day, points] of Object.entries(object(profile.days))) {
      if (!POPULAR_DAYS.some(known => known === day) || !Array.isArray(points)) throw Error('Invalid activity day');
      const hours = new Set<number>();
      for (const point of points) {
        if (!Array.isArray(point) || point.length !== 2) throw Error('Invalid activity point');
        const hour = integer(point[0]), percent = number(point[1]);
        if (hour > 23 || percent > 100 || hours.has(hour)) throw Error('Invalid or duplicate activity hour');
        hours.add(hour);
        activity.push({ id: key(activityAudit.id, placeId, day, hour), sourceId: text(activityAudit.id),
          subject: { kind: 'business', id: `google-place:${placeId}`, name: text(profile.name) },
          metric: 'relative_shop_activity', unit: 'percent', basis: 'relative_index', value: percent, rawValue: percent, bounds: null, period: null,
          dimensions: { day, hour, denominator: 'This business own usual weekly peak' } });
      }
    }
  }
  const seenSources = new Set<string>();
  const publicRows = sources.flatMap(source => {
    if (seenSources.has(source.receipt.id)) throw Error('Duplicate acquisition source');
    seenSources.add(source.receipt.id);
    if (source.receipt.records !== source.records.length) throw Error('Incomplete acquisition');
    if (source.receipt.id === 'sncf-annual') return sncfAnnualObservations(source, station.uic);
    if (source.receipt.id === 'idfm-daily') return idfmDailyObservations(source, station.idfm);
    if (source.receipt.id === 'idfm-hourly') return idfmHourlyObservations(source, station.idfm);
    throw Error('Unknown acquisition source');
  });
  const facts = Array.isArray(snapshot.facts) ? snapshot.facts.map(object).filter(f => typeof f.content === 'string' && /visiteurs?|visitors?|visits?|footfall|fr[eé]quentation/i.test(f.content)) : [];
  return {
    schema: 'fater-traffic-data-v1', propertyId, organisationId, generatedAt, inputSha256,
    places: places.map(census => ({ census: structuredClone(census), activityProfile: profileMap.has(String(census.placeId)) ? structuredClone(profileMap.get(String(census.placeId))!) : null })),
    observations: uniqueTrafficObservations([...publicRows, ...activity]), dailyValidationTotals: dailyTrafficTotals(publicRows), hourlyCoverage: hourlyTrafficCoverage(publicRows), annualVisitorStatements: structuredClone(facts),
    sources: [...sources.map(s => s.receipt), {
      id: text(activityAudit.id), provider: 'Saved Fater activity audit', datasetId: 'FATER_FOOTFALL_BUSYNESS_V1',
      url: `fater:data-audit:${text(activityAudit.id)}`, documentationUrl: 'https://support.google.com/business/answer/6263531?hl=en',
      licence: null, retrievedAt: text(snapshot.capturedAt), dataUpdatedAt: typeof activityAudit.updatedAt === 'string' ? activityAudit.updatedAt : null,
      rawHashes: [{ file: 'input.json', sha256: inputSha256 }], records: profiles.length, releaseQuarter: null,
      limitations: ['Per-business capture/provider provenance is retained verbatim in each activityProfile.', 'Activity measurement period is unspecified; audit creation time is not a measurement period.'],
    }],
    coverage: { places: places.length, profiles: profiles.length, activityHours: activity.length, publicObservations: publicRows.length, measuredMallVisitorObservations: 0, measuredShopVisitorObservations: 0 },
    limitations: ['Census includes nearby businesses; it is not an indoor unit count.', 'Station passengers and fare validations measure different populations and are not mall visitors.', 'Hourly IDFM release quarter is known; actual sampling period is unspecified (quarter/semester documentation ambiguity).', 'Google activity is normalized within each business, not between businesses.', 'Annual visitor statements are attributed historical claims, not current measurements or shop calibration.'],
  };
}
